import { makeAutoObservable } from "mobx"
import groupBy from "lodash/groupBy"

import EntityPermissionsAPI, {
  EntityAccessEndpoints,
  GetEntitiesRequest,
} from "@services/entity-permissions.service"
import RootStore from "@store/RootStore"
import { PaginationParams } from "@framework/types/utils"
import { UserAndGroup } from "@framework/types/user"

import EntityPermissionsStore from "./permissions.store"

export default class EntityPermissionsController {
  permissionsStore: EntityPermissionsStore

  constructor(injections: RootStore) {
    this.permissionsStore = injections.entityPermissionsStore

    makeAutoObservable(this)
  }

  loadUnassignedEntities = async (
    params: GetEntitiesRequest,
    reset: boolean = false
  ) => {
    const store = this.permissionsStore.unassignedEntitiesStore

    if (store.isLoading) return

    if (reset) store.data = []

    store.isLoading = true
    store.error = null

    try {
      const response = await EntityPermissionsAPI.getUserAndGroups(params)

      const meta = response.data.pagination

      store.setMetadata({
        total: meta.totalItems,
        totalPages: meta.totalPages,
        pageNum: meta.page,
        pageSize: meta.pageSize,
      })

      const uniqueUsersAndGroups = response.data.data.filter(
        (newUser) =>
          !store.data.some((existingUser) => existingUser.id === newUser.id)
      )

      store.data = [...store.data, ...uniqueUsersAndGroups]
    } catch (error: any) {
      store.error = error.message
      store.data = []
    } finally {
      store.isLoading = false
    }
  }

  loadPermittedEntities = async (
    companyId: string,
    entityType: EntityAccessEndpoints,
    params: PaginationParams,
    reset: boolean = false
  ): Promise<UserAndGroup[] | null> => {
    const store = this.permissionsStore.permittedEntitiesStore
    try {
      store.isLoading = true

      if (reset) store.data = []

      const response = await EntityPermissionsAPI.getEntityPermissions(
        companyId,
        entityType,
        params
      )

      store.setMetadata(response.data.meta)

      const uniqueUsersAndGroups = response.data.data
        .filter(
          (newUser) =>
            !store.data.some((existingUser) => existingUser.id === newUser.id)
        )
        .map((item): UserAndGroup => {
          if (item.group != null)
            return {
              ...item.group,
              type: "group",
            }

          return {
            ...item.user,
            type: "user",
          }
        })

      store.data = [...store.data, ...uniqueUsersAndGroups]
      return store.data
    } catch (error: any) {
      store.error = error.message
      store.data = []
    } finally {
      store.isLoading = false
    }
    return null
  }

  requestReloadPermittedUsersCollection = () => {
    this.permissionsStore.permittedEntitiesStore.requestReload()
  }

  switchAccessToPublic = async (
    entityId: string,
    entityType: EntityAccessEndpoints
  ) => {
    const store = this.permissionsStore

    try {
      store.setLoading(true)

      await EntityPermissionsAPI.withdrawAccess(entityId, entityType, {
        makePublic: true,
      })

      return true
    } catch (error: any) {
      store.error = error.message
    } finally {
      store.setLoading(false)
    }

    return false
  }

  grantAccess = async (
    entityIds: string[],
    entityType: EntityAccessEndpoints,
    entities: UserAndGroup[]
  ) => {
    const store = this.permissionsStore

    try {
      const { user: users = [], group: groups = [] } = groupBy(entities, "type")

      const userIds = users.map((it) => it.id)
      const groupIds = groups.map((it) => it.id)

      store.setLoading(true)

      await EntityPermissionsAPI.grantAccess({
        entityIds,
        entityType,
        userIds,
        groupIds,
      })

      return true
    } catch (error: any) {
      store.error = error.message
    } finally {
      store.setLoading(false)
    }

    return false
  }

  withdrawAccess = async (
    entityId: string,
    entityType: EntityAccessEndpoints,
    entities: UserAndGroup[]
  ) => {
    const store = this.permissionsStore

    try {
      const userIds = entities
        .filter((it) => it.type === "user")
        .map((it) => it.id)

      const groupIds = entities
        .filter((it) => it.type === "group")
        .map((it) => it.id)

      store.setLoading(true)

      await EntityPermissionsAPI.withdrawAccess(entityId, entityType, {
        userIds,
        groupIds,
        makePublic: false,
      })

      return true
    } catch (error: any) {
      store.error = error.message
    } finally {
      store.setLoading(false)
    }

    return false
  }
}
